import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SelectField } from "@/components/common/SelectField";
import { useTran } from "@/hooks";

const defaultLabels = {
  itemsPerPage: "Items per page",
  previous: "Previous",
  next: "Next",
  pageLabel: ({ page, totalPages }) => `Page ${page} of ${totalPages}`,
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PaginationControls = ({
  page = 1,
  pageSize = 5,
  total = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 15],
  disabled = false,
  labels = {},
  className,
}) => {
  const mergedLabels = { ...defaultLabels, ...labels };
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const currentPage = clamp(page, 1, totalPages);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  const pageSizeValue = String(pageSize);
  const sizeOptions = Array.from(new Set(pageSizeOptions)).sort(
    (a, b) => a - b,
  );
  const { t } = useTran();

  const handlePageChange = (nextPage) => {
    if (!onPageChange) return;
    onPageChange(clamp(nextPage, 1, totalPages));
  };

  const handlePageSizeChange = (nextValue) => {
    if (!onPageSizeChange) return;
    const nextSize = Number(nextValue);
    if (!Number.isNaN(nextSize)) {
      onPageSizeChange(nextSize);
    }
  };
  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span>{mergedLabels.itemsPerPage}</span>
        <SelectField
          value={pageSizeValue}
          onValueChange={handlePageSizeChange}
          options={sizeOptions}
          selectClassName="w-[90px]"
          className="space-y-0"
          disabled={disabled}
        />
      </div>

      <div className="flex items-center gap-3">
        <span>{mergedLabels.pageLabel({ page: currentPage, totalPages })}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={disabled || !canGoPrev}
          >
            <ChevronLeft className="h-4 w-4" />
            {mergedLabels.previous}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={disabled || !canGoNext}
          >
            {mergedLabels.next}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaginationControls;
